import { useMemo } from "react";
import { get, useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { classNames } from "helpers/commonHelpers";
import testIds from "constants/testIds";
import DatePicker from "components/v2/DatePicker/DatePicker";
import Select from "components/v2/Select/Select";
import TimeInput from "components/v2/TimeInput/TimeInput";
import { useGetCountries } from "api/queries/common/useGetCountries";
import ScheduleFieldErrorBadge from "@RequestFormV2/components/Badges/ScheduleFieldErrorBadge";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save";
import {
  DELIVERY_SCHEDULE_ORDER_ERROR_PATH,
  validateDeliveryAfterPickup,
} from "@RequestFormV2/sections/Schedule/validation/validateSchedule";
import { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { useRelationalOnChangeValidation } from "@RequestFormV2/hooks/useRelationalOnChangeValidation";
import { stopTestId } from "@RequestFormV2/utils/testIds";
import { StopPlanningSlice } from "@RequestFormV2/sections/Schedule/hooks/useStopPlanning";

interface StopDateTimeFieldsProps {
  planning: StopPlanningSlice;
  stopIndex: number;
  lockDateFields?: boolean;
}

type StopDateTimePairProps = {
  dateName: string;
  timeName: string;
  dateLabel: string;
  timeTestId: string;
  dateDisabled?: boolean;
  countryCode?: string;
  onDateSelect: () => void;
  onTimeSelect: () => void;
};

function StopDateTimePair({
  dateName,
  timeName,
  dateLabel,
  timeTestId,
  dateDisabled = false,
  countryCode,
  onDateSelect,
  onTimeSelect,
}: StopDateTimePairProps) {
  const intl = useIntl();
  const timeLabel = intl.formatMessage({ id: "general.time", defaultMessage: "Time" });

  return (
    <div className="rfv2-schedule-datetime">
      <div className="rfv2-schedule-datetime__field">
        <label className={classNames("time-input-v2__label", "required")}>
          {dateLabel}
        </label>
        <DatePicker
          name={dateName}
          selectionMode="single"
          numberOfMonths={1}
          alignment="right"
          fullWidth
          required
          openOnFocus
          disabled={dateDisabled}
          countryCode={countryCode}
          onDateSelect={onDateSelect}
        />
      </div>
      <div className="rfv2-schedule-datetime__field">
        <TimeInput name={timeName} label={timeLabel} required testId={timeTestId} onTimeSelect={onTimeSelect} />
      </div>
    </div>
  );
}

export default function StopDateTimeFields({ planning, stopIndex, lockDateFields = false }: StopDateTimeFieldsProps) {
  const intl = useIntl();
  const {
    formState: { errors },
    getValues,
    setError,
  } = useFormContext();
  const saveContext = useRequestFormSaveOrNull();
  const { fieldPaths, isAsap, isAfter, isBetween, isBefore, handleAfterDateTimeChange, handleBeforeDateTimeChange, timezones } =
    planning;

  const handleFieldSave = (fieldName: string, onFieldChange?: () => void) => {
    onFieldChange?.();
    saveContext?.saveField(fieldName, getValues(fieldName)).catch(() => undefined);
  };

  const dateLabel = intl.formatMessage({ id: "general.date", defaultMessage: "Date" });
  const deliveryScheduleError = stopIndex === 1 ? get(errors, DELIVERY_SCHEDULE_ORDER_ERROR_PATH) : undefined;
  const stops = useWatch({ name: "schedule.stops" });
  const stopCountryId = useWatch({ name: `schedule.stops.${stopIndex}.country_id` });
  const stopCountryName = useWatch({ name: `schedule.stops.${stopIndex}.country_name` });
  const stopPortCountry = useWatch({ name: `schedule.stops.${stopIndex}.port_country` });
  const { data: countries = [] } = useGetCountries({
    useV2: true,
    refetchOnMount: false,
  });

  const countryCode = useMemo(() => {
    if (stopCountryId != null && stopCountryId !== "") {
      const byId = countries.find((entry) => String(entry.id) === String(stopCountryId));
      if (byId?.code) {
        return byId.code;
      }
    }
    const countryName = String(stopCountryName || stopPortCountry || "")
      .trim()
      .toLowerCase();
    if (!countryName) {
      return undefined;
    }
    const byName = countries.find(
      (entry) =>
        entry.name?.trim().toLowerCase() === countryName || entry.label?.trim().toLowerCase() === countryName,
    );
    return byName?.code || undefined;
  }, [countries, stopCountryId, stopCountryName, stopPortCountry]);

  useRelationalOnChangeValidation(
    (clear) => validateDeliveryAfterPickup((stops as ScheduleStopValue[]) ?? [], intl, setError, clear),
    [stops],
    stopIndex === 1,
  );

  if (isAsap) return null;

  return (
    <div className="rfv2-schedule-datetime-fields">
      <div className="rfv2-three-column-grid">
        {(isAfter || isBetween) && (
          <StopDateTimePair
            dateName={fieldPaths.stop_after_date}
            timeName={fieldPaths.stop_after_time}
            dateLabel={
              isBetween
                ? intl.formatMessage({ id: "new-request.schedule.specifyHoursFrom", defaultMessage: "From" })
                : dateLabel
            }
            dateDisabled={lockDateFields}
            countryCode={countryCode}
            timeTestId={stopTestId(testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_AFTER_TIME_PREFIX, stopIndex)}
            onDateSelect={() => handleFieldSave(fieldPaths.stop_after_date, handleAfterDateTimeChange)}
            onTimeSelect={() => handleFieldSave(fieldPaths.stop_after_time, handleAfterDateTimeChange)}
          />
        )}

        {(isBefore || isBetween) && (
          <StopDateTimePair
            dateName={fieldPaths.stop_before_date}
            timeName={fieldPaths.stop_before_time}
            dateLabel={
              isBetween ? intl.formatMessage({ id: "new-request.schedule.specifyHoursTo", defaultMessage: "To" }) : dateLabel
            }
            dateDisabled={lockDateFields}
            countryCode={countryCode}
            timeTestId={stopTestId(testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_BEFORE_TIME_PREFIX, stopIndex)}
            onDateSelect={() => handleFieldSave(fieldPaths.stop_before_date, handleBeforeDateTimeChange)}
            onTimeSelect={() => handleFieldSave(fieldPaths.stop_before_time, handleBeforeDateTimeChange)}
          />
        )}

        <Select
          name={fieldPaths.stop_timezone}
          label={intl.formatMessage({
            id: "new-request.schedule.addressBookDialog.timezone",
            defaultMessage: "Timezone",
          })}
          options={timezones}
          required
          isClearable
          testId={stopTestId(testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_TIMEZONE_PREFIX, stopIndex)}
        />
      </div>

      {deliveryScheduleError?.message && (
        <div>
          <ScheduleFieldErrorBadge message={String(deliveryScheduleError.message)} />
        </div>
      )}
    </div>
  );
}
